export type AdminArgument = string | boolean | number;
export interface AdminWorkspace {
  readonly owner: string;
  call<T = unknown>(method: string, ...args: AdminArgument[]): Promise<T>;
  dispose(): void;
}
type KmpClient = { [method: string]: (...args: unknown[]) => unknown };
type AdminExports = {
  IbercarAdminClient: new (base: string, key: string, project: string, database: string, store: unknown, flush: () => Promise<void>, pricingStore: unknown, readCacheStore: unknown) => KmpClient;
  ServiceJournalStorage: new (read: () => string | null, write: (value: string) => void, clear: () => void) => unknown;
};

/** Cada cliente conserva JWT solo en memoria y un diario cifrado vinculado al UID Firebase. */
export async function createAdminWorkspace(owner: string): Promise<AdminWorkspace> {
  const [{ EncryptedJournal }, { kotlinModule }, { getFirebase }, { adminConfig }] = await Promise.all([
    import('@ibercar/native-bridge'), import('@ibercar/native-bridge/compiled'), import('../firebase/firebase'), import('../config/env'),
  ]);
  let active = true;
  const check = () => {
    if (!active || getFirebase().auth.currentUser?.uid !== owner) throw new Error('La cuenta ha cambiado. Vuelve a abrir esta sección.');
  };
  check();
  const journal = await EncryptedJournal.open(owner, 'admin-services');
  let pricingJournal: Awaited<ReturnType<typeof EncryptedJournal.open>> | null = null;
  let readCache: Awaited<ReturnType<typeof EncryptedJournal.open>> | null = null;
  let client: KmpClient | null = null;
  try {
    check();
    pricingJournal = await EncryptedJournal.open(owner, 'admin-pricing'); check();
    // Una copia de consulta opcional no condiciona los diarios duraderos de comandos.
    try { readCache = await EncryptedJournal.open(owner, 'admin-read-cache'); } catch { readCache = null; }
    check();
    const pricing = pricingJournal;
    const cache = readCache;
    const exported = kotlinModule.eu.ibercar.admin as unknown as AdminExports;
    const base = adminConfig.apiBaseUrl.replace(/\/+$/, '').replace(/\/api\/v1$/, '') + '/api/v1/';
    const store = new exported.ServiceJournalStorage(() => journal.read(), (value) => journal.write(value), () => journal.clear());
    const pricingStore = new exported.ServiceJournalStorage(() => pricing.read(), (value) => pricing.write(value), () => pricing.clear());
    const cacheStore = cache ? new exported.ServiceJournalStorage(() => cache.read(), (value) => cache.write(value), () => cache.clear()) : null;
    const flush = async () => { check(); await journal.flush(); await pricing.flush(); check(); };
    client = new exported.IbercarAdminClient(base, '', '', '', store, flush, pricingStore, cacheStore);
    const user = getFirebase().auth.currentUser;
    if (!user || user.uid !== owner) throw new Error('Inicia sesión de nuevo.');
    const idToken = await user.getIdToken(); check();
    const identity = JSON.parse(String(await client['firebaseLogin']!(idToken))) as { id?: string; role?: string; profile_complete?: boolean };
    check();
    if (!identity.id || identity.role !== 'admin') throw new Error('El servidor no ha confirmado acceso de administrador.');
    if (identity.profile_complete !== true) throw new Error('Completa los datos de Mi cuenta antes de continuar.');
    const current = client;
    return {
      owner,
      async call<T>(method: string, ...args: AdminArgument[]): Promise<T> {
        check();
        if (!/^[a-zA-Z][a-zA-Z0-9]*$/.test(method) || typeof current[method] !== 'function' || ['dispose', 'logout', 'firebaseLogin'].includes(method)) throw new Error('Operación no disponible.');
        const result = await current[method](...args);
        // El fallo de la copia local nunca oculta un ACK ni convierte la copia en autoridad.
        // KMP conserva la antigüedad, caduca a las 24 h y exige lectura fresca al mutar.
        try { await cache?.flush(); } catch { /* La respuesta actual sigue siendo válida. */ }
        // El recibo ya confirmado debe seguir siendo visible si falló guardar su ACK.
        // La barrera beforeMutation sigue bloqueando cualquier nuevo envío.
        check();
        if (method !== 'pendingService') {
          try { await flush(); }
          catch (error) {
            check();
            // KMP ya comprobó versión, ámbito y contenido del ACK de publicación.
            if (method === 'publishPricing' && typeof result === 'string') {
              const confirmed = JSON.parse(result) as { snapshot?: unknown; message?: string };
              if (confirmed.snapshot) return { ...confirmed, message: `${confirmed.message || 'Publicación confirmada.'} No se pudo actualizar el borrador local; vuelve a abrir la sección para comprobarlo.`, localStorageWarning: true } as T;
            }
            throw error;
          }
        }
        check();
        if (typeof result !== 'string') return (result ?? null) as T;
        try { return JSON.parse(result) as T; } catch { return result as T; }
      },
      dispose() { active = false; current['dispose']?.(); journal.close(); pricing.close(); cache?.close(); },
    };
  } catch (error) { active = false; client?.['dispose']?.(); journal.close(); pricingJournal?.close(); readCache?.close(); throw error; }
}

export function workspaceError(error: unknown): string {
  return error instanceof Error ? error.message : typeof error === 'object' && error && 'message' in error ? String(error.message) : 'No se pudo completar la operación. Actualiza y vuelve a intentarlo.';
}
